/**
 * Poki Game Events for day/night and evolution-tier funnels.
 * `PokiSDK.measure(category, what, action)` — see developers.poki.com/guide/game-events.
 *
 * Stages alternate day then night inside one `currentCycleIndex` (0 = first day):
 *   1 first day, 2 first night, 3 second day, 4 second night, …
 * Day number is `cycleIndex * 2 + 1`. Night number is `cycleIndex * 2 + 2`.
 * One stage is open at a time: start, then complete or fail.
 *
 * A tier `what` is the 1-based chain position (1 = Cell). The only action is
 * `reached`, once per player, when that tier is first discovered.
 */

/**
 * @returns {{ openStage: string | null }}
 */
export function createPokiProgressState() {
  return {
    openStage: null,
  };
}

/**
 * @param {number} cycleIndex
 * @returns {number}
 */
function cycleIndexOf(cycleIndex) {
  var cycle = Math.floor(Number(cycleIndex) || 0);
  return cycle > 0 ? cycle : 0;
}

/**
 * @param {number} cycleIndex 0-based day/night cycle
 * @returns {string} first day is "1", second day is "3"
 */
export function pokiDayStageWhat(cycleIndex) {
  return String(cycleIndexOf(cycleIndex) * 2 + 1);
}

/**
 * @param {number} cycleIndex 0-based day/night cycle
 * @returns {string} first night is "2", second night is "4"
 */
export function pokiNightStageWhat(cycleIndex) {
  return String(cycleIndexOf(cycleIndex) * 2 + 2);
}

/**
 * @param {{ openStage: string | null }} state
 * @param {"start" | "complete" | "fail"} action
 * @param {number} cycleIndex
 * @param {"day" | "night"} [half]
 * @returns {Array<{ category: string, what: string, action: string }>}
 */
export function stageProgressEvents(state, action, cycleIndex, half) {
  var events = [];
  if (!state) return events;
  if (action === "fail") {
    if (state.openStage == null) return events;
    var failed = state.openStage;
    state.openStage = null;
    events.push({ category: "stage", what: failed, action: "fail" });
    return events;
  }
  var what = half === "night" ? pokiNightStageWhat(cycleIndex) : pokiDayStageWhat(cycleIndex);
  if (action === "start") {
    if (state.openStage != null) return events;
    state.openStage = what;
    events.push({ category: "stage", what: what, action: "start" });
    return events;
  }
  if (action === "complete" && state.openStage === what) {
    state.openStage = null;
    events.push({ category: "stage", what: what, action: "complete" });
  }
  return events;
}

/**
 * Finish the open day and start that cycle's night.
 * A resume that is already in Merge only starts the night.
 * @param {{ openStage: string | null }} state
 * @param {number} cycleIndex
 * @returns {Array<{ category: string, what: string, action: string }>}
 */
export function enterNightStageEvents(state, cycleIndex) {
  var events = [];
  if (!state) return events;
  var dayWhat = pokiDayStageWhat(cycleIndex);
  var nightWhat = pokiNightStageWhat(cycleIndex);
  if (state.openStage === dayWhat) {
    state.openStage = null;
    events.push({ category: "stage", what: dayWhat, action: "complete" });
  }
  if (state.openStage != null) return events;
  state.openStage = nightWhat;
  events.push({ category: "stage", what: nightWhat, action: "start" });
  return events;
}

/**
 * One lifetime discovery. `chainIndex` 0 is Cell (`what` "1").
 * @param {number} chainIndex 0-based active evolution chain index
 * @returns {Array<{ category: string, what: string, action: string }>}
 */
export function tierReachedEvent(chainIndex) {
  var idx = Math.floor(Number(chainIndex));
  if (!isFinite(idx) || idx < 0) return [];
  return [{ category: "tier", what: String(idx + 1), action: "reached" }];
}
